/**
 * 全局错误管理器
 * 用于统一管理错误弹窗的显示
 */

type ErrorHandler = (message: string, title?: string) => void;

let errorHandler: ErrorHandler | null = null;

/**
 * 注册全局错误处理器
 */
export function registerErrorHandler(handler: ErrorHandler) {
  errorHandler = handler;
}

/**
 * 显示错误（API 层面调用）
 * 带防抖机制，避免短时间内重复弹出相同的错误
 */
const DEBOUNCE_MS = 1000; // 1秒防抖
let lastError: { message: string; title: string; timestamp: number } | null = null;

export function showGlobalError(message: string, title: string = '错误') {
  try {
    const now = Date.now();
    
    // 确保消息是字符串
    const safeMessage = typeof message === 'string' ? message : String(message);
    const safeTitle = typeof title === 'string' ? title : '错误';
    
    // 检查是否是重复的错误（相同消息且在防抖时间内）
    if (lastError && 
        lastError.message === safeMessage && 
        lastError.title === safeTitle &&
        now - lastError.timestamp < DEBOUNCE_MS) {
      // 忽略重复的错误
      return;
    }
    
    // 记录这次错误
    lastError = { message: safeMessage, title: safeTitle, timestamp: now };
    
    if (errorHandler) {
      try {
        errorHandler(safeMessage, safeTitle);
      } catch (err) {
        // 如果错误处理器本身出错，fallback 到 alert
        console.error('[ErrorManager] Error handler failed:', err);
        alert(`${safeTitle}: ${safeMessage.substring(0, 500)}`);
      }
    } else {
      // 如果没有注册处理器，fallback 到 alert
      console.error('[ErrorManager] 未注册错误处理器，使用 alert:', safeMessage);
      alert(`${safeTitle}: ${safeMessage.substring(0, 500)}`);
    }
  } catch (err) {
    // 如果整个函数出错，至少输出到控制台
    console.error('[ErrorManager] showGlobalError failed:', err);
    console.error('[ErrorManager] Original message:', message);
    try {
      alert('发生错误，请查看控制台');
    } catch {
      // 如果连 alert 都失败，至少输出到控制台
      console.error('[ErrorManager] Cannot show error, all methods failed');
    }
  }
}

/**
 * 显示提示信息（非错误）
 * 用于显示用户操作相关的提示，如"用户取消支付"
 */
export function showGlobalInfo(message: string, title: string = '提示') {
  showGlobalError(message, title);
}

